"use client";

import { useClientAccess } from "@/lib/hooks/auth/use-client-access";
import { ClientAccessLoading } from "./ClientAccessLoading";
import { ClientAccessGuard } from "./ClientAccessGuard";
import { usePathname } from "next/navigation";

interface ClientAccessWrapperProps {
  children: React.ReactNode;
}

/**
 * Main wrapper component that orchestrates client access checking and rendering
 * Uses the existing simple client access hook with separated UI components
 */
export function ClientAccessWrapper({ children }: ClientAccessWrapperProps) {
  const pathname = usePathname();

  // Skip client access check for internal routes that don't need it
  const skipAccessCheck = pathname === "/chat";

  if (skipAccessCheck) {
    return <>{children}</>;
  }

  // Only call the hook if we're not skipping the check
  const { hasContactAccess, isLoading, isClientUser, appUser } = useClientAccess();

  // Show loading while checking access
  if (isLoading && isClientUser) {
    return <ClientAccessLoading />;
  }

  return (
    <ClientAccessGuard
      isClientUser={isClientUser}
      hasContactAccess={hasContactAccess}
      userEmail={appUser?.email || ""}
    >
      {children}
    </ClientAccessGuard>
  );
}
